import { useAuth0 } from "@auth0/auth0-react";
import { Theme, ThemeProvider } from '@mui/material/styles';
import * as React from 'react';
import { useCookies } from 'react-cookie';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { PortalTextCommon } from '../Models/Api/strongbox.financialportal';
import { CookieSessionData } from '../Models/CookieSessionData';
import { ApplicationState } from '../Store';
import { actionCreators as AppSettingsActions, GetLoginSessionSlidingLifetimeInSeconds } from '../Store/AppSettings';
import {
    CustomerFinancialsTarget,
    DirectLinkParameters,
    GetImportCustomerFinancialsTarget,
    GetImportingDirect,
    GetImportMap,
    actionCreators as ImportFinancialsActions,
    ImportMap,
} from '../Store/ImportFinancials';
import { GetPortalText, RetrieveLoginText, RetrieveText } from '../Store/PortalAssets';
import { BrandConfig, GetBrandConfig, TenantActionCreators } from '../Store/Tenant';
import {
    GetPortalBusyState,
    GetRefreshStateTicker,
    GetShowDataCollectionLimitDetails,
    MillisecondCountLastUserInteraction,
    actionCreators as UIStateActions,
} from '../Store/UIState';
import { actionCreators as UserActions } from '../Store/User';
import {
    auls_info_cookie_name,
    FrontPageTextSectionIds,
    InlineStylingValues,
    pathConstants,
    UpdateUIStateTimeout,
} from '../Utils/Constants';
import { CreateStdTheme } from '../Utils/Style';
import {
    DataCollectionLimitDetailsModal,
} from './DataCollectionLimitDetailsModal/DataCollectionLimitDetailsModal';
import { ErrorBanner, ErrorState } from './ErrorBanner/ErrorBanner';
import { FinancialsImportV2 } from './FinancialsImport/FinancialsImportV2';
import { BusyWindowWrapper } from './LoadingMessageWithIndicator/BusyWindowWrapper';
import { PortalFooter } from './PortalFooter';
import { PortalHeader } from './PortalHeader';

type InjectedReduxState = {
    portalBusy: boolean;
    financialsTarget: CustomerFinancialsTarget | undefined;
    directLinkParameters: DirectLinkParameters | undefined;
    importMap: ImportMap[];
    brandConfig: BrandConfig;
    lastUserInteractionMS: number;
    sessionSlidingLifetimeSeconds: number;
    uiRefreshTicker: number;
    commonText?: PortalTextCommon;
    showDataCollectionLimitDetails: boolean;
};

type InjectedActionCreators = typeof AppSettingsActions & typeof UserActions & typeof UIStateActions & typeof ImportFinancialsActions & typeof TenantActionCreators;

type LenderPortalProps = {
    hideMenu?: boolean;
    hideLoggedInUser?: boolean;
    children?: any;
    hideFooter?: boolean;
    disableUIRefreshTiming?: boolean;
    isLoginPage?: boolean;
    centeredLogoOnlyInheader?: boolean;
    hideHelp?: boolean;
}

type Props = LenderPortalProps & InjectedActionCreators & InjectedReduxState;

const LenderPortalComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        directLinkParameters,
        financialsTarget,
        importMap,
        hideFooter,
        lastUserInteractionMS,
        UpdateLastUserInteraction,
        UpdateUIStateTimer,
        sessionSlidingLifetimeSeconds,
        uiRefreshTicker,
        disableUIRefreshTiming,
        SetPortalWorking,
        SetPortalIdle,
        isLoginPage,
        commonText,
        showDataCollectionLimitDetails,
        GetDataCollectionLimit,
        centeredLogoOnlyInheader,
        hideHelp,
    } = props;

    const navigate = useNavigate();

    const refreshIsDisabled = React.useMemo(() => {
        if (disableUIRefreshTiming === undefined) {
            return false;
        }
        return disableUIRefreshTiming;
    }, [disableUIRefreshTiming])

    const [cookies] = useCookies([auls_info_cookie_name]);
    const { logout } = useAuth0();

    const [uiStateUpdate, setUiStateUpdate] = React.useState<boolean>(false);
    const [cookieSessionData, setSessionCookieData] = React.useState<CookieSessionData | undefined>(undefined);

    const [muiTheme] = React.useState<Theme>(() => {
        return CreateStdTheme(props.brandConfig);
    });

    const [errorState, setErrorState] = React.useState<ErrorState | undefined>(undefined);

    const [updateUserInteraction, setUpdateUserInteraction] = React.useState<boolean>(false);

    const showFooter = React.useMemo(() => {
        return (hideFooter === undefined) ? false : !hideFooter;
    }, [hideFooter])

    const fetchRequiredData = async (): Promise<void> => {
        const workingDescriptor = 'RetrievingFrontPageData';

        // Back end caches text so repeated calls happen quick. 
        SetPortalWorking(workingDescriptor);
        try {
            // get common text first as it's needed for a bunch of stuff

            await RetrieveLoginText();
            await RetrieveText(FrontPageTextSectionIds);

            await GetDataCollectionLimit(false);
        } finally {
            SetPortalIdle(workingDescriptor);
        }
    }

    React.useEffect(() => {
        if (!isLoginPage) {
            fetchRequiredData();
        }

        // No dependencies, this should run on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (!refreshIsDisabled) {
            const timeNow = Date.now();

            let pauseUpdates = false;

            // Check the time the path (url in the browser) last changed. If it's been longer than
            // half a sliding session interval than pause execution of the timer.
            // Cookie update of the session expiration will not occur until we're past the 
            // halfway point of the a session interval so this will shut down authenticated 
            // api calls before it's reset and result in the cookie actually expiring.
            //
            // Don't worry about the absolute time that represents half of the current interval.
            // Merely having an update greater than half the interval is sufficient. Because
            // the cookie can be updated without making a network call, these aren't necessarily
            // the same thing.

            if ((!!cookieSessionData) && (!!cookieSessionData.slidingExpirationTime)) {
                const sessionSlidingLifetimeMS = sessionSlidingLifetimeSeconds * 1000;

                if (timeNow - lastUserInteractionMS > (sessionSlidingLifetimeMS / 2)) {
                    pauseUpdates = true;
                }
            }

            if ((uiStateUpdate) && (!!cookieSessionData)) {
                if (!pauseUpdates) {
                    UpdateUIStateTimer(cookieSessionData);
                }
                setUiStateUpdate(false);
            }
        }

        // These are the dependencies I actually want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uiStateUpdate, cookieSessionData, sessionSlidingLifetimeSeconds, lastUserInteractionMS]);

    React.useEffect(() => {
        if (uiStateUpdate && updateUserInteraction) {
            UpdateLastUserInteraction();
            setUpdateUserInteraction(false);
        }

        // These are the dependencies I actually want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uiStateUpdate, updateUserInteraction]);

    React.useEffect(() => {
        if (!refreshIsDisabled) {
            // Depending on what window is loaded we may or may not be making periodic api calls that will help keep the 
            // sliding session cookie alive. Low cost ping here to make sure it keeps going.

            // IMPORTANT:  Commenting this out as what it used to do is fairly nuanced.  now that there's
            // another call here that uses authentication, it will keep the refresh timer alive.
            // If for any reason, the extra calls are moved or removed or whatever, you need to
            // put this Ping back.

            // Ping();

            GetDataCollectionLimit(false);
        }

        // These are the dependencies I actually want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uiRefreshTicker]);

    // Lint will complain about the calls to setSessionCookieData however there are sufficient
    // guards to ensure this won't result in infinite looping as it checks the values of 
    // liveCookieSessionData to ensure they're different from what we have stored.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        if (!refreshIsDisabled) {
            const liveCookieSessionData = cookies[auls_info_cookie_name] as CookieSessionData;

            if (!cookieSessionData) {
                setSessionCookieData(liveCookieSessionData);
            } else {
                if ((liveCookieSessionData.absoluteExpirationTime !== cookieSessionData.absoluteExpirationTime) ||
                    (liveCookieSessionData.slidingExpirationTime !== cookieSessionData.slidingExpirationTime)) {
                    setSessionCookieData(liveCookieSessionData);
                }
            }
        }
        // Actually do want this to run on every render 
        // eslint-disable-next-line react-hooks/exhaustive-deps
    });

    function ExecuteStateUpdateTimer() {
        setUiStateUpdate(true);
    }

    function TrackMouseDown(this: HTMLElement, ev: Event): void {
        setUpdateUserInteraction(true);
    }

    React.useEffect(() => {
        if (!refreshIsDisabled) {
            window.addEventListener('mousedown', TrackMouseDown);
            const idTimer = setInterval(ExecuteStateUpdateTimer, UpdateUIStateTimeout);

            return () => {
                clearInterval(idTimer);
                window.removeEventListener('mousedown', TrackMouseDown);
            }
        }

        // I want this to execute equivalent to componentDidMount so this is appropriate
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const controlClass = `control-region control-region-lender`;
    const mainDivClass =
        `free-content-region ${controlClass}`;

    const errorActions = React.useMemo(() => {
        if (!(financialsTarget && directLinkParameters)) {
            return undefined;
        } else {
            return (
                [
                    {
                        text: commonText?.dismiss || '',
                        id: 'dismissaction',
                        onAction: (actionId: string) => {
                            setErrorState(undefined);
                        }
                    },
                    {
                        text: commonText?.retry || '',
                        id: 'entercredsandcontinue',
                        onAction: (id: string) => {
                            let keyAccountingPkg = directLinkParameters?.accountingPkg;
                            let connectionIdKey = directLinkParameters?.connectionId;

                            if (!!(keyAccountingPkg || connectionIdKey)) {
                                const map = importMap.find(
                                    mapEntry => {
                                        if (connectionIdKey !== undefined) {
                                            return mapEntry.importRequest.connectionId === connectionIdKey;
                                        } else {
                                            return mapEntry.workspaceId === financialsTarget!.workspaceId &&
                                                mapEntry.accountingPackage.toLowerCase() === keyAccountingPkg?.toLowerCase();
                                        }
                                    }
                                );
                                if (!!map) {
                                    props.RetryImportRequest(map);
                                }
                            }
                            setErrorState(undefined);
                        }
                    }
                ]
            )
        }
        // These are the state that actually affect the execution.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [financialsTarget, directLinkParameters, importMap]);

    return (
        <div>
            <ThemeProvider theme={muiTheme}>
                <PortalHeader
                    flow={'lender'}
                    centeredLogoOnly={centeredLogoOnlyInheader}
                    hideMenu={props.hideMenu}
                    logout={() => {
                        props.LogUserOut(logout);
                    }}
                    settings={() => {
                        navigate(pathConstants.settingsDefault);
                    }}
                    dataCollectionReport={() => {
                        navigate(pathConstants.dataCollectionReport);
                    }}
                    notificationSettings={() => {
                        navigate(pathConstants.settingsNotifications);
                    }}
                    showLoggedInUser={props.hideLoggedInUser !== true}
                    showHelp={hideHelp !== true}
                    onPortalLanguageChange={(languageCode) => fetchRequiredData()}
                />
                {
                    !!errorState && (
                        <div style={{ width: '100%' }} className={mainDivClass}>
                            <div
                                style={{
                                    height: 'fit-content',
                                    marginTop: InlineStylingValues.BasicSectionSeparation,
                                }}
                                className={'contained-content'}
                            >
                                <ErrorBanner
                                    errorState={{ ...errorState, actions: errorActions }}
                                    onDefaultActionButton={() => {
                                        setErrorState(undefined);
                                    }}
                                    commonText={commonText}
                                />
                            </div>
                        </div>
                    )
                }
                <div className={`control-region control-region-lender`} >
                    <BusyWindowWrapper
                        portalBusy={props.portalBusy}
                        workingText={commonText?.working || ''}
                    >
                        {
                            props.children
                        }
                    </BusyWindowWrapper>
                </div>
                {
                    showFooter && (<PortalFooter />)
                }

                <FinancialsImportV2 />

                {
                    showDataCollectionLimitDetails && (
                        <DataCollectionLimitDetailsModal />
                    )
                }
            </ThemeProvider>
        </div>
    );
}

export const LenderPortal = connect<InjectedReduxState, InjectedActionCreators, LenderPortalProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            portalBusy: GetPortalBusyState(appState),
            financialsTarget: GetImportCustomerFinancialsTarget(appState),
            directLinkParameters: GetImportingDirect(appState),
            importMap: GetImportMap(appState),
            brandConfig: GetBrandConfig(appState),
            sessionSlidingLifetimeSeconds: GetLoginSessionSlidingLifetimeInSeconds(appState),
            lastUserInteractionMS: MillisecondCountLastUserInteraction(appState),
            uiRefreshTicker: GetRefreshStateTicker(appState),
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            showDataCollectionLimitDetails: GetShowDataCollectionLimitDetails(appState),
        };

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...AppSettingsActions,
            ...UserActions,
            ...UIStateActions,
            ...ImportFinancialsActions,
            ...TenantActionCreators
        },
        dispatch
    )
)(LenderPortalComponent);
